import React, { useEffect, useContext, useState, useRef, useMemo } from "react";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../BaseUrl";

//functions
import {
  _t,
  getCookie,
  getSystemSettings,
} from "../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//3rd party packages
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Moment from "react-moment";
import moment from "moment";
import Select from "react-select";
import ReactHTMLTableToExcel from "react-html-table-to-excel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

//pages & includes
import ReportSidebar from "./ReportSidebar";

//context consumer
import { SettingsContext } from "../../../../contexts/Settings";
import CustomSpinLoader from "../../../components/CustomSpinLoader";
import { RestaurantContext } from "../../../../contexts/Restaurant";

const StoreRequisitionReport = () => {
  const { t } = useTranslation();
  let {
    loading,
    setLoading,
    setDataPaginating,
    generalSettings,
  } = useContext(SettingsContext);
  let { branchListWeb, branchForSearch } = useContext(RestaurantContext);


  const [reportData, setReportData] = useState(null);
  const componentRef = useRef();
  const componentRef1 = useRef();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [requisitionReport, setRequisitionReport] = useState({
    from_branch: null,
    to_branch: null,
    reportType: "detail",
  });
  const [generatedReport, setGeneratedReport] = useState(false);
  const [siteName, setSiteName] = useState("");

  useEffect(() => {
    setSiteName(getSystemSettings(generalSettings, "siteName"));

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, [generalSettings]);

  const REPORT_TYPE_OPTIONS = useMemo(
    () => [
      { label: t("Summary Report"), value: "summary", name: "sr_report_type" },
      { label: t("Detail Report"), value: "detail", name: "sr_report_type" },
    ],
    []
  );

  const handleChangeOptions = (type, value) => {
    setRequisitionReport((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleFromBranch = (fromBranch) => {
    setRequisitionReport({
      ...requisitionReport,
      from_branch: fromBranch,
      to_branch: null
    });
  };

  const handleToBranch = (toBranch) => {
    setRequisitionReport({
      ...requisitionReport,
      to_branch: toBranch,
    });
  };
  
  const getExcelReport = () => {
    setReportData(null);
    setGeneratedReport(false);
    if (startDate !== null && endDate !== null && requisitionReport.from_branch) {
      getReport();
    } else {
      toast.error(`${_t(t("Please select from date, to date, branch"))}`, {
        position: "bottom-center",
        closeButton: false,
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        className: "text-center toast-notification",
      });
    }
  };

  const getReport = async () => {
    setDataPaginating(true);
    const fromDate = moment(startDate).format("YYYY-MM-DD");
    const toDate = moment(endDate).format("YYYY-MM-DD");
    const url = BASE_URL + "/settings/get-storerequisition-report";

    const payload = {
      from_date: fromDate,
      to_date: toDate,
      from_branch: requisitionReport.from_branch ? requisitionReport.from_branch.id : "",
      to_branch: requisitionReport.to_branch ? requisitionReport.to_branch?.id : "",
      type: requisitionReport.reportType,
    };

    return axios
      .post(url, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        setGeneratedReport(true);
        setReportData(res.data);
        setDataPaginating(false);
        if (requisitionReport.reportType === "summary") {
          componentRef1.current.handleDownload();
        } else {
          componentRef.current.handleDownload();
        }
      })
      .catch((error) => {
        setDataPaginating(false);
        toast.error(`${_t(t("Please try again"))}`, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
      });
  };

  return (
    <>
      <Helmet>
        <title>{_t(t("Store Requisition Report"))}</title>
      </Helmet>
      <ReactHTMLTableToExcel
        id="sr-detail-table-xls-button"
        className="download-table-xls-button btn btn-success float-right mr-3 invisible"
        table="table-to-xls-sr-detail"
        filename="Store Requisition Detail"
        sheet="sheet 1"
        buttonText="Store Requisition Detail"
        ref={componentRef}
      />

      <ReactHTMLTableToExcel
        id="sr-summary-table-xls-button"
        className="download-table-xls-button btn btn-success float-right mr-3 invisible"
        table="table-to-xls-sr-summary"
        filename="Store Requisition Summary"
        sheet="sheet 1"
        buttonText="Store Requisition Summary"
        ref={componentRef1}
      />

      {/* Detail Excel Report */}
      <div className="d-none">
        <div>
          <div className="fk-print">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  {reportData !== null &&
                    reportData !== undefined &&
                    generatedReport === true && (
                      <>
                        <div className="row gx-2 justify-content-center t-pb-15 t-pt-15 report">
                          <div className="col-12 t-mb-15 mb-md-0 table-responsive">
                            <table
                              className="table table-bordered table-hover min-table-height mt-3"
                              id="table-to-xls-sr-detail"
                            >
                              <div className="page-break"></div>
                              <thead className="align-middle">
                                <tr style={{ border: "0px" }}>
                                  <th colspan="9" style={{ border: "0px" }}>
                                    <div className="row">
                                      <div className="col-12">
                                        <h3 className="text-center mb-0">
                                          {siteName}
                                        </h3>
                                        <h3 className="text-center mt-0">
                                          Store Requisition Report
                                        </h3>
                                        <p className="text-center">
                                          FROM&nbsp;
                                          <Moment format="LL">
                                            {startDate}
                                          </Moment>
                                          &nbsp;TO&nbsp;
                                          <Moment format="LL">{endDate}</Moment>
                                        </p>
                                      </div>
                                    </div>
                                  </th>
                                </tr>
                                <tr>
                                  <th scope="col">{_t(t("Document Id"))}</th>
                                  <th scope="col">{_t(t("Document Date"))}</th>
                                  <th scope="col">{_t(t("From Branch"))}</th>
                                  <th scope="col">{_t(t("To Branch"))}</th>
                                  <th scope="col">{_t(t("Status"))}</th>
                                  <th scope="col">{_t(t("Item Name"))}</th>
                                  <th scope="col">{_t(t("Unit"))}</th>
                                  <th scope="col">{_t(t("Qty"))}</th>
                                  <th scope="col">{_t(t("Remarks"))}</th>
                                </tr>
                              </thead>
                              <tbody className="align-middle">
                                {reportData?.length &&
                                  reportData.map((item, index) => {
                                    const details =
                                      item.store_requisition_detail?.length
                                        ? item.store_requisition_detail
                                        : [{}];
                                    return details.map((detail, dIndex) => (
                                      <tr key={`${index}-${dIndex}`}>
                                        <td scope="row">
                                          {item.document_identity}
                                        </td>
                                        <td scope="row">
                                          <Moment format="LL">
                                            {item.document_date}
                                          </Moment>
                                        </td>
                                        <td scope="row">
                                          {item.from_branch_name}
                                        </td>
                                        <td scope="row">
                                          {item.to_branch_name}
                                        </td>
                                        <td scope="row">{item.is_post}</td>
                                        <td scope="row">
                                          {detail.item_name || ""}
                                        </td>
                                        <td scope="row">
                                          {detail.unit_name || ""}
                                        </td>
                                        <td scope="row">{detail.qty || ""}</td>
                                        <td scope="row">{item.remarks}</td>
                                      </tr>
                                    ));
                                  })}
                              </tbody>

                              <tfoot style={{ border: "0px" }}>
                                <tr style={{ border: "0px" }}>
                                  <td
                                    colspan="9"
                                    className="text-center"
                                    style={{ border: "0px" }}
                                  >
                                    <h5 className="mt-3">
                                      Powered by indolj.pk
                                    </h5>
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      </>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Excel Report */}
      <div className="d-none">
        <div>
          <div className="fk-print">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  {reportData !== null &&
                    reportData !== undefined &&
                    generatedReport === true && (
                      <>
                        <div className="row gx-2 justify-content-center t-pb-15 t-pt-15 report">
                          <div className="col-12 t-mb-15 mb-md-0 table-responsive">
                            <table
                              className="table table-bordered table-hover min-table-height mt-3"
                              id="table-to-xls-sr-summary"
                            >
                              <div className="page-break"></div>
                              <thead className="align-middle">
                                <tr style={{ border: "0px" }}>
                                  <th colspan="6" style={{ border: "0px" }}>
                                    <div className="row">
                                      <div className="col-12">
                                        <h3 className="text-center mb-0">
                                          {siteName}
                                        </h3>
                                        <h3 className="text-center mt-0">
                                          Store Requisition Report
                                        </h3>
                                        <p className="text-center">
                                          FROM&nbsp;
                                          <Moment format="LL">
                                            {startDate}
                                          </Moment>
                                          &nbsp;TO&nbsp;
                                          <Moment format="LL">{endDate}</Moment>
                                        </p>
                                      </div>
                                    </div>
                                  </th>
                                </tr>
                                <tr>
                                  <th scope="col">{_t(t("Document Id"))}</th>
                                  <th scope="col">{_t(t("Document Date"))}</th>
                                  <th scope="col">{_t(t("From Branch"))}</th>
                                  <th scope="col">{_t(t("To Branch"))}</th>
                                  <th scope="col">{_t(t("Status"))}</th>
                                  <th scope="col">{_t(t("Remarks"))}</th>
                                </tr>
                              </thead>
                              <tbody className="align-middle">
                                {reportData?.length &&
                                  reportData.map((item, index) => (
                                    <tr key={index}>
                                      <td scope="row">
                                        {item.document_identity}
                                      </td>
                                      <td scope="row">
                                        <Moment format="LL">
                                          {item.document_date}
                                        </Moment>
                                      </td>
                                      <td scope="row">
                                        {item.from_branch_name}
                                      </td>
                                      <td scope="row">
                                        {item.to_branch_name}
                                      </td>
                                      <td scope="row">{item.is_post}</td>
                                      <td scope="row">{item.remarks}</td>
                                    </tr>
                                  ))}
                              </tbody>

                              <tfoot style={{ border: "0px" }}>
                                <tr style={{ border: "0px" }}>
                                  <td
                                    colspan="6"
                                    className="text-center"
                                    style={{ border: "0px" }}
                                  >
                                    <h5 className="mt-3">
                                      Powered by indolj.pk
                                    </h5>
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      </>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ReportSidebar />
            </div>

            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div
                  className="fk-scroll--pos-menu table-bottom-info-hide"
                  data-simplebar
                >
                  <div className="t-pl-15 t-pr-15">
                    <CustomSpinLoader loading={loading} />
                    <div key="smtp-form">
                      <div className="gx-2 align-items-center t-pt-15 t-pb-15">
                        <div className="row t-mb-15 mb-md-0">
                          <ul className="t-list fk-breadcrumb col-6">
                            <li className="fk-breadcrumb__list">
                              <span className="pl-3 t-link fk-breadcrumb__link text-capitalize">
                                {_t(t("Store Requisition Report"))}
                              </span>
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-md-2 d-md-block">
                          <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            className="form-control"
                            placeholderText={_t(t("From date"))}
                            shouldCloseOnSelect={false}
                          />
                        </div>

                        <div className="col-md-2 d-md-block">
                          <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            peekNextMonth
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            className="form-control"
                            placeholderText={_t(t("To date"))}
                            shouldCloseOnSelect={false}
                          />
                        </div>

                        <div className="col-2">
                          <Select
                            options={branchForSearch ? branchForSearch : []}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            value={requisitionReport.from_branch}
                            classNamePrefix="select"
                            className="table-text"
                            onChange={handleFromBranch}
                            maxMenuHeight="200px"
                            placeholder={_t(t("From branch")) + ".."}
                          />
                        </div>

                        <div className="col-2">
                          <Select
                            options={branchListWeb ? branchListWeb.filter((item)=>item.id!== requisitionReport?.from_branch?.id) : []}
                            getOptionLabel={(option) => option.name}
                            getOptionValue={(option) => option.id}
                            value={requisitionReport.to_branch}
                            classNamePrefix="select"
                            className="table-text"
                            onChange={handleToBranch}
                            maxMenuHeight="200px"
                            placeholder={_t(t("To branch")) + ".."}
                          />
                        </div>


                        <div className="col-2 d-flex flex-wrap">
                          {REPORT_TYPE_OPTIONS.map((opt) => (
                            <div
                              key={opt.value}
                              className="d-inline d-flex align-items-center mr-1"
                            >
                              <input
                                type="radio"
                                id={`sr_${opt.value}`}
                                name={opt.name}
                                value={opt.value}
                                checked={
                                  requisitionReport?.reportType === opt.value
                                }
                                onChange={() =>
                                  handleChangeOptions("reportType", opt.value)
                                }
                                className="form-check-input pointer-cursor"
                              />
                              <label
                                htmlFor={`sr_${opt.value}`}
                                style={{ fontSize: "16px", borderRadius: "5px" }}
                                className="form-radio-label pointer-cursor ml-2 pt-1 text-primary"
                              >
                                {opt.label}
                              </label>
                            </div>
                          ))}
                        </div>

                        <div className="col-2 t-mb-15 mb-md-0 text-right">
                          <button
                            className="ml-1 btn btn-block btn-primary sm-text py-2 rounded"
                            onClick={() => getExcelReport()}
                          >
                            GENERATE REPORT
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default StoreRequisitionReport;
